import React, { useContext, useEffect, useState } from 'react';
import { css, keyframes } from '@emotion/css';
import PropTypes from 'prop-types';
import { cond, get, has, invoke, isEmpty, map } from 'lodash';
import Asset from '../Order/Asset';
import { SubstitutionsContext, SubstitutionsContextType } from './EditSubstitutionLink';
import { SidePanelContext, SidePanelContextType } from '../../common/SidePanel/SidePanel';
import appLabels from '../../utils/appLabels';

const mainContainerStyle = css`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  padding: 0;
  border-bottom: 1px solid #f1f0ee;
`;

const itemContainerStyle = css`
  position: relative;
  display: flex;
  padding-bottom: 1rem;

  & button {
    background: #fff;
    color: #7b1a27;
    border: 1px solid #7b1a27;
    border-radius: 6.25rem;
    padding: 0.5rem 0.75rem;
    width: 6.25rem;
    height: 2.4rem;
    cursor: pointer;
    font-size: 1rem;
    font-weight: 600;
    margin-top: 0.5rem;
  }

  & button[disabled] {
    color: #4b4b4b;
    background-color: #dedede;
    border: 1px solid #ccc;
  }
`;

const descriptionContainerStyle = css`
  display: flex;
  align-items: flex-start;
`;

const itemInfoStyle = css`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 0 1rem;
  justify-content: center;

  @media (max-width: 641px) {
    padding: 0;
  }
`;

const ItemImageContainer = css`
  width: 3.75rem;
  height: 3.75rem;

  @media (max-width: 641px) {
    width: 4rem;
    height: 4rem;
    min-width: 4rem;
  }
`;

const assetStyle = css`
  @media (max-width: 641px) {
    width: 4rem;
    height: 4rem;
  }
`;

const itemPriceStyle = css`
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.38;
  color: #80250c;
`;

const itemNameStyle = css`
  font-weight: 600;
  line-height: 1rem;
  flex: 1 0 0;
  font-size: 0.875rem;
  color: #2A2928;

  @media (max-width: 641px) {
    flex: 1 0 40%;
  }
`;

const substitutedProductStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  padding-bottom: 1.5rem;

  & svg {
    margin: 0.75rem 0.5rem;
  }
`;

const substitutionsListStyle = css`
  overflow: hidden auto;
  flex: 1;
`;

const searchBarContainer = css`
  position: relative;
  display: flex;
  flex-direction: column;
  margin: 1rem 0.25rem 1rem 0;

  & .icon {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    height: 100%;
    width: 2rem;
    display: flex;
    align-items: center;
  }

  & svg {
    cursor: pointer;
  }
`;

const searchBarStyle = css`
  appearance: none;
  padding: 0.5rem 0.25rem 0.5rem 1rem;
  border: solid 1px #7d7b78;
  background-color: #fff;
  border-radius: 100px;
`;

const loadingAnim = keyframes`
    0% {background-position: 0 50%;}
    50% {background-position: 100% 50%;}
    100% {background-position: 0 50%;}
`;

const loadingStyle = css`
  & span,
  & svg,
  & .asset,
  & button {
    color: rgba(0, 0, 0, 0);
    background: linear-gradient(270deg, #eeeeee, #cccccc);
    background-size: 400% 400%;
    border: 0;
    animation: ${loadingAnim} 3s ease infinite;
  }

  & svg path {
    opacity: 0;
  }
`;

const substitutionLinkStyle = css`
  padding: 2rem;
  display: flex;
  align-content: center;
  justify-content: center;
  margin: 0;

  & button {
    border: 0;
    background: #fff;
    color: #7b1a27;
    font-size: 1rem;
    text-decoration: underline;
    cursor: pointer;
  }
`;

const substituteItemDiscountMessageStyle = css`
  Display: flex;
  background: #FEF173;
  padding: 12px 10px;
  margin-bottom: 1rem;

  p {
    margin-bottom: 0;
    margin-left: 8px !important;
    font-size: 0.75rem;
  }
  `

const SearchIcon = (props) => {
  const handler = get(props, 'onClick');
  return (
    <div className="icon">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" onClick={handler}>
        <path
          d="M11.048 3.857a7.191 7.191 0 0 1 5.742 11.519l3.854 3.854-1.414 1.414-3.854-3.854a7.19 7.19 0 1 1-4.328-12.932zm0 2a5.19 5.19 0 1 0 0 10.38 5.19 5.19 0 0 0 0-10.379z"
          fill="#4B4B4B"
          fillRule="evenodd"
        />
      </svg>
    </div>
  );
};

const ClearIcon = (props) => {
  const handler = get(props, 'onClick');
  return (
    <div className="icon">
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="19" onClick={handler}>
        <path
          d="m15.57 2.314 1.074 1.047-6.725 6.892 6.39 6.71-1.085 1.034-6.353-6.67-6.513 6.677-1.074-1.048 6.552-6.716-6.558-6.885L2.364 2.32l6.52 6.846 6.686-6.852z"
          fill="#4B4B4B"
          fillRule="evenodd"
        />
      </svg>
    </div>
  );
};

const DiscountIcon = (props) => {
  return (
    <div className="icon">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M15.9912 10.4332C15.6782 10.162 15.2045 10.1958 14.9332 10.5088L8.43322 18.0088C8.16194 18.3218 8.19577 18.7955 8.50879 19.0668C8.82181 19.3381 9.29547 19.3042 9.56675 18.9912L16.0668 11.4912C16.338 11.1782 16.3042 10.7045 15.9912 10.4332Z" fill="#1F1E1E" />
        <path d="M7.75 12.25C7.75 13.0784 8.42157 13.75 9.25 13.75C10.0784 13.75 10.75 13.0784 10.75 12.25C10.75 11.4216 10.0784 10.75 9.25 10.75C8.42157 10.75 7.75 11.4216 7.75 12.25Z" fill="#1F1E1E" />
        <path d="M14.75 19.25C13.9216 19.25 13.25 18.5784 13.25 17.75C13.25 16.9216 13.9216 16.25 14.75 16.25C15.5784 16.25 16.25 16.9216 16.25 17.75C16.25 18.5784 15.5784 19.25 14.75 19.25Z" fill="#1F1E1E" />
        <path fill-rule="evenodd" clip-rule="evenodd" d="M11.25 1C11.25 0.585786 11.5858 0.25 12 0.25C12.4142 0.25 12.75 0.585786 12.75 1V1.4662C13.2332 1.56471 13.7013 1.75945 14.1245 2.05041L19.558 5.78592C20.3042 6.29895 20.75 7.14647 20.75 8.05203V21C20.75 22.5188 19.5188 23.75 18 23.75H6C4.48122 23.75 3.25 22.5188 3.25 21V8.05203C3.25 7.14647 3.69582 6.29895 4.44205 5.78592L9.87552 2.05041C10.2987 1.75945 10.7668 1.56471 11.25 1.4662V1ZM11.25 3.01911C11.067 3.08381 10.8905 3.17293 10.7253 3.28647L5.29184 7.02198C4.95265 7.25518 4.75 7.64041 4.75 8.05203V21C4.75 21.6903 5.30964 22.25 6 22.25H18C18.6904 22.25 19.25 21.6903 19.25 21V8.05203C19.25 7.64041 19.0474 7.25518 18.7082 7.02198L13.2747 3.28647C13.1095 3.17293 12.933 3.08381 12.75 3.01911V4.20067C13.1984 4.46003 13.5 4.94479 13.5 5.5C13.5 6.32843 12.8284 7 12 7C11.1716 7 10.5 6.32843 10.5 5.5C10.5 4.94479 10.8016 4.46003 11.25 4.20067V3.01911Z" fill="#1F1E1E" />
      </svg>
 </div>
  );
};

const SearchBar = () => {
  const currentSubstitutionsContext = useContext(SubstitutionsContext) as SubstitutionsContextType;
  const [isResultsListEmpty, setIsResultsListEmpty] = useState(true);
  const searchToken = get(currentSubstitutionsContext, 'searchToken');
  const results = get(currentSubstitutionsContext, 'substitutionData');
  const [userInitiatedFetch, setUserInitiatedFetch] = useState(false);

  useEffect(() => {
    if (isEmpty(searchToken)) {
      clearResults();
    }
  }, [searchToken]);

  useEffect(() => {
    setIsResultsListEmpty(results.length <= 0);
  }, [results]);

  const clearResults = () => {
    invoke(currentSubstitutionsContext, 'setSearchToken', '');
    invoke(currentSubstitutionsContext, 'clearSubstitutions', '');
    setUserInitiatedFetch(false);
  };

  const loadResults = () => {
    invoke(currentSubstitutionsContext, 'loadSubstitutions', searchToken);
  };

  const handleChange = (evt) => {
    const val = get(evt, 'target.value');
    invoke(currentSubstitutionsContext, 'setSearchToken', val);
  };

  const handleKey = (evt) => {
    if (get(evt, 'key') === 'Enter' && !isEmpty(searchToken)) {
      setUserInitiatedFetch(true);
      loadResults();
    }
  };

  return (
    <div className={searchBarContainer}>
      <input
        className={searchBarStyle}
        type="text"
        placeholder="Search"
        onChange={handleChange}
        onKeyDown={handleKey}
        value={searchToken}
      />
      {!isResultsListEmpty && userInitiatedFetch ? (
        <ClearIcon onClick={() => clearResults()} />
      ) : (
        <SearchIcon
          onClick={() => {
            setUserInitiatedFetch(true);
            loadResults();
          }}
        />
      )}
    </div>
  );
};

SearchBar.propTypes = {
  onChange: PropTypes.func,
};

const SubstitutedProduct = (props) => {
  const item = get(props, 'item');
  const loading = get(props, 'loading');
  const isBestAvailable = get(props, 'isBestAvailable');

  return (
    <div className={substitutedProductStyle}>
      <div className={ItemImageContainer}>
        <Asset productBpc={get(item, 'productBpn')} loading={loading} />
      </div>

      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25">
        <path
          d="M8.048 8.604h9.961l-2.935 2.951a.447.447 0 0 0 .314.76.439.439 0 0 0 .313-.13l3.692-3.712a.446.446 0 0 0 .055-.067l.01-.019c.011-.019.022-.038.03-.058.004-.009.006-.019.009-.028.006-.018.013-.036.017-.055a.438.438 0 0 0-.001-.176c-.003-.02-.01-.037-.016-.056l-.008-.027a.663.663 0 0 0-.031-.059l-.01-.018a.511.511 0 0 0-.055-.068L15.7 4.131a.439.439 0 0 0-.626 0 .447.447 0 0 0 0 .63l2.934 2.951h-9.96C5.265 7.712 3 9.99 3 12.79c0 .246.199.445.443.445.245 0 .443-.2.443-.445 0-2.308 1.867-4.185 4.162-4.185zm12.509 4.162c-.245 0-.443.2-.443.446 0 2.307-1.867 4.185-4.162 4.185H5.991l2.935-2.952c.173-.174.173-.456 0-.63s-.454-.174-.627 0l-3.692 3.712a.409.409 0 0 0-.055.068l-.01.02a.376.376 0 0 0-.03.056c-.005.01-.007.019-.01.029-.006.018-.012.035-.016.054a.43.43 0 0 0 .017.231l.008.029c.008.02.02.038.03.057l.011.02a.502.502 0 0 0 .055.067l3.691 3.712a.441.441 0 0 0 .627 0 .447.447 0 0 0 0-.63l-2.934-2.952h9.961c2.783 0 5.048-2.277 5.048-5.076a.444.444 0 0 0-.443-.446z"
          fill="#4B4B4B"
          fillRule="nonzero"
        />
      </svg>

      <div>
        {isBestAvailable ? (
          <span>If this item goes out of stock, we’ll substitute it with your selection or best available.</span>
        ) : (
          <span>If this item goes out of stock, we’ll substitute it with your selection.</span>
        )}
      </div>
    </div>
  );
};

SubstitutedProduct.propTypes = {
  item: PropTypes.object,
  loading: PropTypes.bool,
  isBestAvailable: PropTypes.bool,
};

const SubstitutionItem = (props) => {
  const item = get(props, 'item');
  const loading = get(props, 'loading');
  const currentSubstitutionsContext = useContext(SubstitutionsContext) as SubstitutionsContextType;
  const currentSidePanelContext = useContext(SidePanelContext) as SidePanelContextType;

  const selectProduct = async () => {
    await invoke(currentSubstitutionsContext, 'postSelectedProduct', item);
    await invoke(currentSidePanelContext, 'closePanel');
  };

  const isSelected = get(item, 'itemIsSelected', false);
  const itemPrice = get(item, 'itemPrice');
  const itemDescription = get(item, 'itemDescription');

  return (
    <div className={itemContainerStyle}>
      <div className={ItemImageContainer}>
        <Asset productBpc={get(item, 'itemBpn')} className={assetStyle} loading={loading} />
      </div>

      <div className={itemInfoStyle}>
        <span className={itemPriceStyle}>{`$${itemPrice} / ea`}</span>
        <span className={`body-text ${itemNameStyle}`}>{itemDescription}</span>
      </div>

      <div>
        <button disabled={isSelected} onClick={selectProduct}>
          {isSelected ? 'Selected' : 'Select'}
        </button>
      </div>
    </div>
  );
};

SubstitutionItem.propTypes = {
  item: PropTypes.object,
  loading: PropTypes.bool,
};

const DonNotSubstituteLink = (props) => {
  const currentSubstitutionsContext = useContext(SubstitutionsContext) as SubstitutionsContextType;
  const currentSidePanelContext = useContext(SidePanelContext) as SidePanelContextType;
  const item = get(props, 'product');

  const postDoNotSubstitute = async () => {
    await invoke(currentSubstitutionsContext, 'postDoNotSubstitute', item);
    await invoke(currentSidePanelContext, 'closePanel');
  };

  return (
    <div>
      <p className={substitutionLinkStyle}>
        <button onClick={postDoNotSubstitute}>Don't substitute</button>
      </p>
    </div>
  );
};

const SubstitutionsList = (props) => {
  const item = get(props, 'product');
  const selectedProduct = get(props, 'selectedProduct');
  const loading = get(props, 'loading');
  const apiError = get(props, 'apiError');
  const searchError = get(props, 'searchError');
  const substitutions = get(props, 'substitutions', []);
  const isBestAvailable = get(props, 'isBestAvailable', false);

  const content = cond([
    [
      () => apiError,
      () => {
        return (
          <div className={`${substitutionsListStyle} ${loading ? loadingStyle : ''}`}>
            <p
              className={css`
                font-size: 0.875rem;
                margin-top: 1rem;
              `}
            >
              <span>Search for an item to substitute with.</span>
            </p>
          </div>
        );
      },
    ],
    [
      () => searchError,
      () => {
        return (
          <div className={`${substitutionsListStyle} ${loading ? loadingStyle : ''}`}>
            <p>
              <span
                className={css`
                  font-size: 0.875rem;
                `}
              >
                Search is temporarily unavailable. Please try again later.
              </span>
            </p>
          </div>
        );
      },
    ],
    [
      () => true,
      () => {
        return (
          <>
            <SubstitutedProduct item={item} loading={loading} isBestAvailable={isBestAvailable} />
            <div className={substituteItemDiscountMessageStyle}>
              <DiscountIcon/>
              <p>{appLabels.ScheduledItems.substituteItemDiscountMessageText}</p>
            </div>
            <div className={`${substitutionsListStyle} ${loading ? loadingStyle : ''}`}>
              {has(selectedProduct, 'itemBpn') ? <SubstitutionItem loading={false} item={selectedProduct} /> : null}
              {map(substitutions, (item) =>
                item.itemIsSelected ? null : <SubstitutionItem loading={loading} item={item} />,
              )}
            </div>
          </>
        );
      },
    ],
  ]);

  return (
    <div className={`${mainContainerStyle}`}>
      <SearchBar />
      {content()}
      <DonNotSubstituteLink product={item} />
    </div>
  );
};

SubstitutionsList.propTypes = {
  product: PropTypes.object,
  substitutions: PropTypes.array,
  loading: PropTypes.bool,
  apiError: PropTypes.bool,
  searchError: PropTypes.bool,
  selectedProduct: PropTypes.object,
  isBestAvailable: PropTypes.bool,
};

export default SubstitutionsList;
